import { ProductList as SourceProductList } from 'SourceComponent/ProductList/ProductList.component';
import ProductListPage from 'Component/ProductListPage';
import CmsBlock from 'Component/CmsBlock';
import { createPortal } from 'react-dom';
import React from 'react';

export class ProductList extends SourceProductList {
    // Keep references to each page's DOM node:
    nodes = {};

    /**
     * Override renderProductPage so we can store references to each page's DOM node
     * and possibly set a data attribute if needed.
     */
    renderProductPage = ([key, items = []]) => {
        const pageNumber = +key;
        return this.renderPage({
            pageNumber,
            items,
            key,
            wrapperRef: (node) => {
                this.nodes[pageNumber] = node;
                if (node) node.setAttribute('data-page', pageNumber);
            }
        });
    };

    /**
     * A custom method to load the previous page while "expanding upward."
     * 1) Identify the current minimum page (e.g. 2),
     * 2) measure its top offset (so we know where the user is looking),
     * 3) call the container's loadPrevPage,
     * 4) in componentDidUpdate, measure the new offset and scroll by the difference.
     */
    loadPreviousPageUpwards() {
        const { isLoading, pages } = this.props;
        if (isLoading) return;

        // Which page is currently at the top?
        const keys = Object.keys(pages).map(Number);
        const minPage = keys.length ? Math.min(...keys) : 1;
        if (minPage <= 1) return; // no previous page to load

        // We'll "anchor" to that top page's node (e.g. page2).
        const anchorNode = this.nodes[minPage];
        if (!anchorNode) {
            // fallback if no node found
            this._oldAnchorOffset = window.scrollY;
            this._anchorPage = minPage;
        } else {
            // measure the top offset in the current viewport
            const rect = anchorNode.getBoundingClientRect();
            this._oldAnchorOffset = rect.top + window.scrollY;
            this._anchorPage = minPage;
        }

        // We'll detect in componentDidUpdate that a new page was added (minPage changed)
        // and then do the "delta scroll."
        this._loadingPreviousUpwards = true;

        // Call the container's action to actually load the previous page:
        this.props.loadPrevPage();
    }

    /**
     * After the new page is loaded, measure the new top offset of that same anchor page
     * and scroll by the difference, so the user sees the same portion of pageN.
     */
    componentDidUpdate(prevProps) {
        // If we are in the middle of an "expand upward" flow:
        if (this._loadingPreviousUpwards) {
            // see if new page arrived
            const oldKeys = Object.keys(prevProps.pages).map(Number);
            const newKeys = Object.keys(this.props.pages).map(Number);
            const oldMinPage = oldKeys.length ? Math.min(...oldKeys) : 1;
            const newMinPage = newKeys.length ? Math.min(...newKeys) : 1;

            // If we actually loaded a new min page (e.g. from 2 down to 1):
            if (newMinPage < oldMinPage) {
                // measure the anchor page's new top offset
                const anchorNode = this.nodes[this._anchorPage];
                if (anchorNode) {
                    const rect = anchorNode.getBoundingClientRect();
                    const newAnchorOffset = rect.top + window.scrollY;

                    // the difference
                    const diff = newAnchorOffset - this._oldAnchorOffset;

                    // we scroll by that difference to keep the user in place
                    // setTimeout or requestAnimationFrame can help ensure DOM is fully reflowed
                    window.requestAnimationFrame(() => {
                        window.scrollBy(0, diff);
                    });
                }

                // done
                this._loadingPreviousUpwards = false;
            }
        }

        super.componentDidUpdate && super.componentDidUpdate(prevProps);
    }

    /**
     * A custom "Load Previous" button that calls loadPreviousPageUpwards.
     */
    renderLoadPrevButton() {
        const { pages, isLoading } = this.props;
        if (isLoading) return null;

        const keys = Object.keys(pages).map(Number);
        const minPage = keys.length ? Math.min(...keys) : 1;
        if (minPage > 1) {
            return (
                <div
                    block="ProductList"
                    elem="LoadButton"
                    onClick={ this.loadPreviousPageUpwards.bind(this) }
                    onKeyUp={ this.loadPreviousPageUpwards.bind(this) }
                    role="button"
                    tabIndex="0"
                >
                    { __('PRREVIOUS PRODUCTS') }
                </div>
            );
        }
        return null;
    }

    /* ========== The rest is your existing code ========== */

    renderTitle() {
        const { title } = this.props;
        if (!title) {
            return null;
        }
        return <h2 block="Section" elem="Title"><span>{ title }</span></h2>;
    }

    renderPages() {
        const { pages, isLoading } = this.props;
        if (isLoading || !Object.keys(pages).length) {
            return this.renderPage();
        }
        return Object.entries(pages).map(this.renderProductPage);
    }

    renderNoProducts() {
        return (
            <div block="ProductList">
                <div block="ProductList" elem="ProductsMissing">
                    <h2>{ __("Your search showed no results") }</h2>
                    <h2>{ __("Try a new search") }</h2>
                    <h3>{ __("We recommend you below some of our BESTSELLER products") }:</h3>
                    <CmsBlock identifier="catalog-noresults" />
                </div>
            </div>
        );
    }

    renderPage(props = {}) {
        const {
            isInfiniteLoaderEnabled,
            loadPage,
            isLoading,
            isVisible,
            isWidget,
            currentPage,
            mix,
            category
        } = this.props;

        const newProps = this._processProps(props);
        return (
            <ProductListPage
                key={ currentPage }
                isInfiniteLoaderEnabled={ isInfiniteLoaderEnabled }
                updatePages={ loadPage }
                isLoading={ isLoading }
                isVisible={ isVisible }
                isWidget={ isWidget }
                mix={ mix }
                category={ category }
                { ...newProps }
            />
        );
    }

    renderRelPrevNext() {
        return null;
    }

    renderLoadMoreButton() {
        const { isVisible, loadPage, isLoading } = this.props;
        if (isLoading || !isVisible) return null;
        return (
            <div
                block="ProductList"
                elem="LoadButton"
                onClick={ loadPage }
                onKeyUp={ loadPage }
                role="button"
                tabIndex="0"
            >
                { __('MORE PRODUCTS') }
            </div>
        );
    }

    renderPagination() {
        return null;
    }

    render() {
        const { totalPages, isLoading, mix } = this.props;
        if (!isLoading && totalPages === 0) {
            return this.renderNoProducts();
        }
        return (
            <div block="ProductList" mods={ { isLoading } } mix={ mix }>
                { createPortal(this.renderRelPrevNext(), document.head) }
                { this.renderTitle() }
                { this.renderLoadPrevButton() }
                { this.renderPages() }
                { this.renderLoadMoreButton() }
                { this.renderPagination() }
            </div>
        );
    }
}

export default ProductList;
